import { meshBounds, useCursor, useGLTF } from "@react-three/drei";
import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";

import { useFrame } from "@react-three/fiber";
import "./cloud-gtlf.scss";

export const CloudGLTF: React.FC<any> = forwardRef((props, ref) => {
  const group = useRef<any>();
  const { scene } = useGLTF("https://s3.tebi.io/rhdev/drone.gltf");
  const [hovered, set] = useState<boolean>(false);
  useCursor(hovered, "pointer");
  useEffect(
    () => void (document.body.style.cursor = hovered ? "pointer" : "auto"),
    [hovered]
  );
  // Make it float in the air when it's opened
  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    if (group.current && group.current.rotation) {
      group.current.position.y = THREE.MathUtils.lerp(
        group.current.position.y,
        Math.cos(t / 10) / 10 + 2,
        0.1
      );
      group.current.rotation.x = THREE.MathUtils.lerp(
        group.current.rotation.x,
        Math.cos(t / 10) / 10 + 0.25,
        0.1
      );
      group.current.rotation.y = THREE.MathUtils.lerp(
        group.current.rotation.y,
        Math.sin(t / 10) / 4,
        0.1
      );
      group.current.rotation.z = THREE.MathUtils.lerp(
        group.current.rotation.z,
        Math.sin(t / 10) / 10,
        0.1
      );
      group.current.position.y = THREE.MathUtils.lerp(
        group.current.position.y,
        (-2 + Math.sin(t)) / 3,
        0.1
      );
    }
  });
  // const group = useRef();

  const copiedScene = useMemo(() => {
    return scene.clone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene.uuid]);

  const onPointerOver = (e: any) => {
    e.stopPropagation();
    set(true);
  };

  const onPointerOut = (e: any) => {
    e.stopPropagation();
    set(false);
  };

  if (props.fast) {
    return (
      <group ref={group} {...props} dispose={null} scale={6}>
        <primitive object={copiedScene} />
      </group>
    );
  }

  return (
    <group
      ref={ref}
      {...props}
      dispose={null}
      onPointerOver={onPointerOver}
      onPointerOut={onPointerOut}
      className="cursor-pointer"
    >
      {copiedScene.children.map((_, i) => (
        <CloudGLTFGroup
          key={i + _.name}
          {...props}
          node={_}
          index={i}
          onHover={() => props.onHover(ref)}
        />
      ))}
    </group>
  );
});

function CloudGLTFGroup({ ...props }) {
  const meshRef = useRef();
  // useBVH(meshRef);

  if (props.node.type === "Group") {
    return (
      <group
        rotation={props.node.rotation}
        position={props.node.position}
        scale={props.node.scale}
        frustumCulled
      >
        {props.node.children.length > 0 &&
          props.node.children.map((_: any, i: any) => (
            <CloudGLTFGroup key={i + _.name} {...props} node={_} index={i} />
          ))}
      </group>
    );
  }
  return (
    <mesh
      geometry={props.node.geometry}
      material={props.node.material}
      rotation={props.node.rotation}
      position={props.node.position}
      raycast={meshBounds}
      scale={props.node.scale}
      onPointerOver={(e) => {
        e.stopPropagation();
        props.onHover(meshRef);
      }}
      onPointerOut={(e) => {
        e.stopPropagation();
        props.onHover(null);
      }}
      onClick={(e) => {
        e.stopPropagation();
        props.onHover(meshRef);
      }}
    >
      {props.node.children.length > 0 &&
        props.node.children.map((_: any, i: any) => (
          <CloudGLTFGroup key={i + _.name} {...props} node={_} index={i} />
        ))}
    </mesh>
  );
}

export default CloudGLTF;
